import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen } from "lucide-react";
import { usePageMeta } from "@/components/pepmuse/usePageMeta";
import { Reveal, Eyebrow } from "@/components/pepmuse/Reveal";
import { DarkButton } from "@/components/pepmuse/Buttons";
import { articleBySlug } from "@/data/articles";

const ARTICLE_BASE = "https://pepmuse.com/resources";

export default function ResourceArticle({ onBuild }) {
  const { slug } = useParams();
  const article = articleBySlug(slug);

  usePageMeta(
    article ? `${article.title} — PepMuse` : "Resources — PepMuse",
    article?.description
  );

  // Article structured data (rich results + AI indexing)
  useEffect(() => {
    if (!article) return undefined;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.articleSchema = "true";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.description,
      datePublished: article.date,
      author: { "@type": "Organization", name: "PepMuse", url: "https://pepmuse.com/" },
      publisher: {
        "@type": "Organization",
        name: "PepMuse",
        logo: { "@type": "ImageObject", url: "https://pepmuse.com/brand-mark.png" },
      },
      mainEntityOfPage: `${ARTICLE_BASE}/${article.slug}`,
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!article) {
    return (
      <section className="pb-24 pt-[140px]">
        <div className="mx-auto max-w-2xl px-5 text-center">
          <h1 className="font-display text-3xl font-extrabold text-paper">Guide not found.</h1>
          <Link
            to="/resources"
            data-testid="article-not-found-back"
            className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-lilac transition-colors hover:text-lilac-bright"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to resources
          </Link>
        </div>
      </section>
    );
  }

  return (
    <article data-testid="article-page" className="pb-24 pt-[120px] sm:pt-[150px]">
      <div className="mx-auto w-full max-w-2xl px-5 sm:px-8">
        <Reveal>
          <Link
            to="/resources"
            data-testid="article-back-link"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-paper/45 transition-colors duration-200 hover:text-lilac focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            All guides
          </Link>
          <p className="mt-8 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-paper/35">
            <BookOpen className="h-3.5 w-3.5 text-lilac" aria-hidden="true" />
            Guide · {article.readMinutes} min read
          </p>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight text-paper sm:text-4xl">
            {article.title}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-paper/60">{article.description}</p>
        </Reveal>

        <div className="mt-10">
          {article.sections.map((s, i) => (
            <Reveal key={i} delay={0.05}>
              <section data-testid={`article-section-${i}`} className="mt-10">
                <h2 className="font-display text-xl font-extrabold tracking-tight text-paper sm:text-2xl">{s.h}</h2>
                {s.paras.map((p, j) => (
                  <p key={j} className="mt-4 text-[15px] leading-relaxed text-paper/65">
                    {p}
                  </p>
                ))}
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-14 rounded-3xl bg-lilac p-8 sm:p-10">
            <Eyebrow tone="dark">PepMuse</Eyebrow>
            <p className="mt-3 font-display text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
              Want this built for you?
            </p>
            <p className="mt-3 text-base text-ink/65">
              Supply, white-label vials, brand, website, and fulfillment — one package, one timeline.
            </p>
            <DarkButton testId="article-cta-button" onClick={() => onBuild?.()} className="mt-6">
              Start your build
            </DarkButton>
          </div>
        </Reveal>
      </div>
    </article>
  );
}
